import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../react-query/constant';
import {
  getOrganizationFeaturesRequest,
  getUserSettingRequest
} from '../helpers/api';
import { getLanguage } from './index';
import { useTranslation } from './LanguageProvider';

/***
 * returns a function that switches the app language and refetches
 * everything, since backend texts (errors, notification_repr, todo_repr)
 * are built per request in the user's language
 */
export const useApplyLanguage = () => {
  const { setLanguage } = useTranslation();
  const queryClient = useQueryClient();

  return useCallback(
    (language) => {
      if (!language || language === getLanguage()) return;
      setLanguage(language);
      queryClient.invalidateQueries();
    },
    [setLanguage, queryClient]
  );
};

/***
 * keeps the app language equal to user_setting.language (the effective
 * language: always "fa" while the organization's bilingual feature is off).
 * Rendered on private routes only.
 */
const LanguageSync = () => {
  const applyLanguage = useApplyLanguage();
  const queryClient = useQueryClient();

  const { refetch } = useQuery(
    [queryKeys.user_setting],
    getUserSettingRequest,
    {
      onSuccess(data) {
        applyLanguage(data?.language);
      }
    }
  );

  // the feature may be switched off/on by the system admin; re-sync whenever
  // organization features are refreshed
  useQuery(['organizationFeatures'], getOrganizationFeaturesRequest, {
    onSuccess() {
      if (!queryClient.isFetching([queryKeys.user_setting])) refetch();
    }
  });

  return null;
};

export default LanguageSync;
